import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import type { Industry } from '../../data/industries';

/*
 * IndustryCard
 * ------------
 * Single card in the /industries "Meet the industries we empower"
 * grid (and shareable by any future page that wants to surface
 * industries in a list — same pattern we used for ServiceCard).
 *
 * Behaviour mirrors ServiceCard verbatim:
 *   • First frame is primed on mount so idle cards don't sit on a
 *     black poster while waiting for hover.
 *   • Hover-to-play semantics: at rest the video is paused; on
 *     hover / focus it starts, on leave / blur it pauses again.
 *     Keeps a grid of 7 <video> elements light on GPU decode.
 *   • Whole card is a single <Link> so anywhere the visitor clicks
 *     (video, title, description, "Learn more" pill) routes to the
 *     industry detail page.
 */

export interface IndustryCardProps {
  industry: Industry;
  animationDelay?: number;
}

export const IndustryCard: React.FC<IndustryCardProps> = ({
  industry,
  animationDelay = 0,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.then(() => {
        if (!v.dataset.hovering) v.pause();
      }).catch(() => {
        // Autoplay blocked — the poster / first frame still shows.
      });
    }
  }, [industry.mediaUrl]);

  const handleEnter = () => {
    const v = videoRef.current;
    if (!v) return;
    v.dataset.hovering = 'true';
    v.play().catch(() => {});
  };

  const handleLeave = () => {
    const v = videoRef.current;
    if (!v) return;
    delete v.dataset.hovering;
    v.pause();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{
        duration: 0.45,
        delay: animationDelay,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <Link
        to={`/industries/${industry.id}`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 rounded-xl"
      >
        <div className="aspect-video overflow-hidden rounded-xl bg-navy-900 mb-5">
          <video
            ref={videoRef}
            src={industry.mediaUrl}
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>

        <h3 className="text-xl md:text-[22px] font-bold text-navy-900 mb-2.5 group-hover:text-accent-700 transition-colors">
          {industry.title}
        </h3>

        <p className="text-[15px] md:text-base text-navy-600 leading-relaxed mb-4 line-clamp-3">
          {industry.heroTagline}
        </p>

        <span className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-accent-600 group-hover:text-accent-700 transition-colors">
          Learn more
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </span>
      </Link>
    </motion.div>
  );
};
